import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  AcademicCapIcon,
  BookOpenIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline'
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts'
import { useAuth } from '@/hooks/useAuth'
import {
  getGrades,
  getStreams,
  getLessons,
  getGradeStats,
  getStreamStats,
  getLessonStats,
  type Grade,
  type Stream,
  type Lesson,
} from '@/api/academicApi'
import { getAttendanceStats } from '@/api/attendanceApi'
import ExportModal from '@/components/ui/ExportModal'
import { type ExportConfig } from '@/utils/exportUtils'
import toast from 'react-hot-toast'

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16']
const DIFFICULTY_COLORS: Record<string, string> = {
  Beginner: '#10B981',
  Intermediate: '#F59E0B',
  Advanced: '#EF4444',
}

const PerformanceReportsPage: React.FC = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [grades, setGrades] = useState<Grade[]>([])
  const [streams, setStreams] = useState<Stream[]>([])
  const [lessons, setLessons] = useState<Lesson[]>([])
  const [gradeStats, setGradeStats] = useState<{
    totalGrades: number
    activeGrades: number
    inactiveGrades: number
    totalStreams: number
  } | null>(null)
  const [streamStats, setStreamStats] = useState<{
    totalStreams: number
    activeStreams: number
    inactiveStreams: number
    byGrade: Record<string, number>
  } | null>(null)
  const [lessonStats, setLessonStats] = useState<{
    totalLessons: number
    activeLessons: number
    inactiveLessons: number
    bySubject: Record<string, number>
    byDifficulty: Record<string, number>
  } | null>(null)
  const [showExportModal, setShowExportModal] = useState(false)

  // Filters
  const [selectedGrade, setSelectedGrade] = useState('')

  useEffect(() => {
    fetchData()
  }, [selectedGrade])

  const fetchData = async () => {
    try {
      setLoading(true)

      const [gradesRes, streamsRes, lessonsRes, gradeStatsRes, streamStatsRes, lessonStatsRes] = await Promise.all([
        getGrades({ limit: 100 }).catch(() => ({ grades: [] })),
        getStreams(selectedGrade ? { gradeId: selectedGrade } : {}).catch(() => []),
        getLessons(selectedGrade ? { streamId: selectedGrade } : {}).catch(() => []),
        getGradeStats().catch(() => null),
        getStreamStats().catch(() => null),
        getLessonStats().catch(() => null),
      ])

      setGrades(gradesRes.grades || [])
      setStreams(Array.isArray(streamsRes) ? streamsRes : [])
      setLessons(Array.isArray(lessonsRes) ? lessonsRes : [])
      setGradeStats(gradeStatsRes)
      setStreamStats(streamStatsRes)
      setLessonStats(lessonStatsRes)
    } catch (error) {
      console.error('Failed to fetch performance data:', error)
      toast.error('Failed to load performance data')
    } finally {
      setLoading(false)
    }
  }

  const getExportConfig = (): ExportConfig => {
    const totalStudents = grades.reduce((acc, grade) => acc + (grade.studentCount || 0), 0)
    const selectedGradeName = grades.find(g => g._id === selectedGrade)?.name

    return {
      title: 'Academic Performance Report',
      subtitle: selectedGradeName ? `Grade: ${selectedGradeName}` : 'All Grades',
      filename: `academic-performance-report-${new Date().toISOString().split('T')[0]}`,
      institution: {
        name: 'SmartTag School',
      },
      generatedAt: new Date().toLocaleString(),
      summary: [
        { label: 'Total Grades', value: gradeStats?.totalGrades || 0 },
        { label: 'Active Grades', value: gradeStats?.activeGrades || 0 },
        { label: 'Total Streams', value: streamStats?.totalStreams || 0 },
        { label: 'Total Lessons', value: lessonStats?.totalLessons || 0 },
        { label: 'Total Students', value: totalStudents },
        { label: 'Subjects', value: Object.keys(lessonStats?.bySubject || {}).length },
      ],
      sections: [
        {
          title: 'Academic Structure Summary',
          columns: [
            { header: 'Category', key: 'category', width: 30 },
            { header: 'Total', key: 'total', width: 15 },
            { header: 'Active', key: 'active', width: 15 },
            { header: 'Inactive', key: 'inactive', width: 15 },
          ],
          data: [
            {
              category: 'Grades',
              total: gradeStats?.totalGrades || 0,
              active: gradeStats?.activeGrades || 0,
              inactive: gradeStats?.inactiveGrades || 0,
            },
            {
              category: 'Streams',
              total: streamStats?.totalStreams || 0,
              active: streamStats?.activeStreams || 0,
              inactive: streamStats?.inactiveStreams || 0,
            },
            {
              category: 'Lessons',
              total: lessonStats?.totalLessons || 0,
              active: lessonStats?.activeLessons || 0,
              inactive: lessonStats?.inactiveLessons || 0,
            },
          ],
        },
        {
          title: 'Grades Overview',
          columns: [
            { header: 'Grade', key: 'name', width: 25 },
            { header: 'Streams', key: 'streams', width: 15 },
            { header: 'Students', key: 'students', width: 15 },
            { header: 'Status', key: 'status', width: 15 },
            { header: 'Created', key: 'created', width: 25 },
          ],
          data: grades.map(grade => ({
            name: grade.name,
            streams: grade.streamCount || 0,
            students: grade.studentCount || 0,
            status: grade.isActive ? 'Active' : 'Inactive',
            created: new Date(grade.createdAt).toLocaleDateString(),
          })),
        },
        {
          title: 'Lessons by Subject',
          columns: [
            { header: 'Subject', key: 'subject', width: 35 },
            { header: 'Count', key: 'count', width: 20 },
            { header: 'Percentage', key: 'percentage', width: 20 },
          ],
          data: Object.entries(lessonStats?.bySubject || {}).map(([subject, count]) => ({
            subject,
            count,
            percentage: lessonStats?.totalLessons
              ? `${((count / lessonStats.totalLessons) * 100).toFixed(1)}%`
              : '0%',
          })),
        },
        {
          title: 'Lessons by Difficulty',
          columns: [
            { header: 'Difficulty', key: 'difficulty', width: 25 },
            { header: 'Count', key: 'count', width: 20 },
            { header: 'Percentage', key: 'percentage', width: 20 },
          ],
          data: Object.entries(lessonStats?.byDifficulty || {}).map(([difficulty, count]) => ({
            difficulty,
            count,
            percentage: lessonStats?.totalLessons
              ? `${((count / lessonStats.totalLessons) * 100).toFixed(1)}%`
              : '0%',
          })),
        },
        {
          title: 'Streams by Grade',
          columns: [
            { header: 'Grade', key: 'grade', width: 30 },
            { header: 'Stream Count', key: 'count', width: 20 },
          ],
          data: Object.entries(streamStats?.byGrade || {}).map(([gradeName, count]) => ({
            grade: gradeName,
            count,
          })),
        },
      ],
    }
  }

  // Prepare chart data
  const gradeDistributionData = grades.map((grade, index) => ({
    name: grade.name,
    streams: grade.streamCount || 0,
    students: grade.studentCount || 0,
    color: COLORS[index % COLORS.length],
  }))

  const lessonsBySubjectData = lessonStats?.bySubject
    ? Object.entries(lessonStats.bySubject).map(([subject, count], index) => ({
        name: subject,
        value: count,
        color: COLORS[index % COLORS.length],
      }))
    : []

  const lessonsByDifficultyData = lessonStats?.byDifficulty
    ? Object.entries(lessonStats.byDifficulty).map(([difficulty, count]) => ({
        name: difficulty,
        value: count,
        color: DIFFICULTY_COLORS[difficulty] || '#6B7280',
      }))
    : []

  const streamsByGradeData = streamStats?.byGrade
    ? Object.entries(streamStats.byGrade).map(([gradeName, count], index) => ({
        name: gradeName,
        value: count,
        color: COLORS[index % COLORS.length],
      }))
    : []

  // Academic metrics for radar chart
  const academicMetrics = [
    { subject: 'Grades', value: gradeStats?.activeGrades || 0, fullMark: Math.max(gradeStats?.totalGrades || 10, 10) },
    { subject: 'Streams', value: streamStats?.activeStreams || 0, fullMark: Math.max(streamStats?.totalStreams || 20, 20) },
    { subject: 'Lessons', value: lessonStats?.activeLessons || 0, fullMark: Math.max(lessonStats?.totalLessons || 50, 50) },
    { subject: 'Beginner', value: lessonStats?.byDifficulty?.['Beginner'] || 0, fullMark: 30 },
    { subject: 'Intermediate', value: lessonStats?.byDifficulty?.['Intermediate'] || 0, fullMark: 30 },
    { subject: 'Advanced', value: lessonStats?.byDifficulty?.['Advanced'] || 0, fullMark: 30 },
  ]

  const totalStudents = grades.reduce((acc, grade) => acc + (grade.studentCount || 0), 0)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Performance Reports</h1>
          <p className="text-gray-600 mt-1">Academic structure and performance analytics</p>
        </div>
        <button
          onClick={() => setShowExportModal(true)}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
          Export Report
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm text-gray-600">Filter by Grade:</span>
          </div>
          <select
            value={selectedGrade}
            onChange={(e) => setSelectedGrade(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Grades</option>
            {grades.map((grade) => (
              <option key={grade._id} value={grade._id}>
                {grade.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <AcademicCapIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-600">Total Grades</p>
              <p className="text-xl font-bold text-gray-900">{gradeStats?.totalGrades || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-600">Active Grades</p>
              <p className="text-xl font-bold text-green-600">{gradeStats?.activeGrades || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <UserGroupIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-gray-600">Total Streams</p>
              <p className="text-xl font-bold text-purple-600">{streamStats?.totalStreams || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <BookOpenIcon className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-gray-600">Total Lessons</p>
              <p className="text-xl font-bold text-amber-600">{lessonStats?.totalLessons || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <UserGroupIcon className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-xs text-gray-600">Total Students</p>
              <p className="text-xl font-bold text-indigo-600">{totalStudents}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-pink-100 rounded-lg">
              <ChartBarIcon className="h-5 w-5 text-pink-600" />
            </div>
            <div>
              <p className="text-xs text-gray-600">Subjects</p>
              <p className="text-xl font-bold text-pink-600">{Object.keys(lessonStats?.bySubject || {}).length}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Grade Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Students & Streams by Grade</h3>
          {gradeDistributionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={gradeDistributionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis yAxisId="left" orientation="left" stroke="#2563EB" />
                <YAxis yAxisId="right" orientation="right" stroke="#10B981" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="students" fill="#2563EB" name="Students" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="streams" fill="#10B981" name="Streams" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">
              No grade data available
            </div>
          )}
        </motion.div>

        {/* Lessons by Subject */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Lessons by Subject</h3>
          {lessonsBySubjectData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={lessonsBySubjectData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {lessonsBySubjectData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">
              No lesson data available
            </div>
          )}
        </motion.div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lessons by Difficulty */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Lessons by Difficulty</h3>
          {lessonsByDifficultyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={lessonsByDifficultyData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={100} />
                <Tooltip />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {lessonsByDifficultyData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">
              No difficulty data available
            </div>
          )}
        </motion.div>

        {/* Academic Overview Radar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Academic Structure Overview</h3>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart cx="50%" cy="50%" outerRadius="80%" data={academicMetrics}>
              <PolarGrid />
              <PolarAngleAxis dataKey="subject" />
              <PolarRadiusAxis angle={30} domain={[0, 'auto']} />
              <Radar
                name="Academic Structure"
                dataKey="value"
                stroke="#2563EB"
                fill="#2563EB"
                fillOpacity={0.5}
              />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Grades Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Grades Overview</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Streams</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Students</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {grades.length > 0 ? (
                grades.map((grade) => (
                  <tr key={grade._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{grade.name}</div>
                      {grade.description && (
                        <div className="text-sm text-gray-500">{grade.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {grade.streamCount || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {grade.studentCount || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        grade.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {grade.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(grade.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    No grades found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
          className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-sm p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100">Academic Coverage</p>
              <p className="text-3xl font-bold mt-1">{gradeStats?.activeGrades || 0}/{gradeStats?.totalGrades || 0}</p>
              <p className="text-sm text-blue-100 mt-1">Active Grades</p>
            </div>
            <AcademicCapIcon className="h-12 w-12 text-blue-200" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-sm p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100">Lesson Activity</p>
              <p className="text-3xl font-bold mt-1">{lessonStats?.activeLessons || 0}</p>
              <p className="text-sm text-green-100 mt-1">Active Lessons</p>
            </div>
            <BookOpenIcon className="h-12 w-12 text-green-200" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3 }}
          className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-sm p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100">Student Ratio</p>
              <p className="text-3xl font-bold mt-1">
                {streamStats?.totalStreams ? Math.round(totalStudents / streamStats.totalStreams) : 0}
              </p>
              <p className="text-sm text-purple-100 mt-1">Students per Stream</p>
            </div>
            <UserGroupIcon className="h-12 w-12 text-purple-200" />
          </div>
        </motion.div>
      </div>

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        config={getExportConfig()}
        title="Export Performance Report"
      />
    </div>
  )
}

export default PerformanceReportsPage
